import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowDown, ArrowUp, Pause, RefreshCw, Zap } from 'lucide-react';
import type { DailyAdaptationData } from '@/lib/analyticsApi';
import { MetricInfoButton } from '@/components/MetricInfoButton';

interface DailyAdaptationBannerProps {
  adaptation?: DailyAdaptationData | null;
  isLoading?: boolean;
  className?: string;
}

const actionConfig: Record<string, {
  icon: typeof ArrowDown;
  accentClass: string;
  borderClass: string;
  glowClass: string;
}> = {
  adjust_intensity: {
    icon: ArrowDown,
    accentClass: 'text-amber-400',
    borderClass: 'border-amber-500/30',
    glowClass: 'bg-amber-500 shadow-[0_0_8px_hsl(35,90%,55%,0.4)]',
  },
  adjust_duration: {
    icon: ArrowDown,
    accentClass: 'text-amber-400',
    borderClass: 'border-amber-500/30',
    glowClass: 'bg-amber-500 shadow-[0_0_8px_hsl(35,90%,55%,0.4)]',
  },
  swap_workout: {
    icon: RefreshCw,
    accentClass: 'text-amber-400',
    borderClass: 'border-amber-500/30',
    glowClass: 'bg-amber-500 shadow-[0_0_8px_hsl(35,90%,55%,0.4)]',
  },
  insert_recovery: {
    icon: Pause,
    accentClass: 'text-blue-400',
    borderClass: 'border-blue-500/30',
    glowClass: 'bg-blue-500 shadow-[0_0_8px_hsl(210,70%,55%,0.4)]',
  },
  insert_rest: {
    icon: Pause,
    accentClass: 'text-red-400',
    borderClass: 'border-red-500/30',
    glowClass: 'bg-red-500 shadow-[0_0_8px_hsl(0,70%,55%,0.4)]',
  },
  upgrade_intensity: {
    icon: ArrowUp,
    accentClass: 'text-green-400',
    borderClass: 'border-green-500/30',
    glowClass: 'bg-green-500 shadow-[0_0_8px_hsl(150,60%,50%,0.4)]',
  },
};

function formatMultiplier(value: number): string {
  if (value === 1.0) return '';
  const pct = Math.round((value - 1.0) * 100);
  return pct > 0 ? `+${pct}%` : `${pct}%`;
}

export function DailyAdaptationBanner({ adaptation, isLoading, className }: DailyAdaptationBannerProps) {
  // Don't render if loading, missing, or no changes
  if (isLoading || !adaptation || !adaptation.has_changes) {
    return null;
  }

  const config = actionConfig[adaptation.action] || {
    icon: AlertTriangle,
    accentClass: 'text-amber-400',
    borderClass: 'border-amber-500/30',
    glowClass: 'bg-amber-500 shadow-[0_0_8px_hsl(35,90%,55%,0.4)]',
  };
  const Icon = config.icon;

  const volLabel = formatMultiplier(adaptation.volume_multiplier);
  const intLabel = formatMultiplier(adaptation.intensity_multiplier);
  const hasModifiers = volLabel || intLabel;

  return (
    <motion.div
      className={cn(
        'relative rounded-lg border bg-white/[0.02] backdrop-blur-sm overflow-hidden',
        config.borderClass,
        className,
      )}
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {/* Left accent */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-[2px]', config.glowClass)} />

      <div className="p-4 pl-5">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <Icon className={cn('h-4 w-4 flex-shrink-0', config.accentClass)} strokeWidth={1.5} />
          <span className={cn('text-[11px] font-mono font-medium tracking-wide uppercase', config.accentClass)}>
            {adaptation.reason_text}
          </span>
          <MetricInfoButton metricKey="adaptationDecision" />

          {/* Modifier pills */}
          {hasModifiers && (
            <div className="flex items-center gap-1.5 ml-auto">
              {volLabel && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/[0.08] bg-white/[0.03] text-slate-400">
                  vol {volLabel}
                  <MetricInfoButton metricKey="volumeMultiplier" />
                </span>
              )}
              {intLabel && (
                <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/[0.08] bg-white/[0.03] text-slate-400">
                  int {intLabel}
                  <MetricInfoButton metricKey="intensityMultiplier" />
                </span>
              )}
            </div>
          )}
        </div>

        {/* Explanation */}
        <p className="text-[12px] font-mono text-slate-400 leading-relaxed">
          {adaptation.explanation}
        </p>

        {/* Session type change */}
        {adaptation.modified_session_type && adaptation.original_session_type && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wider">
              {adaptation.original_session_type}
            </span>
            <Zap className="h-3 w-3 text-slate-600" />
            <span className={cn('text-[10px] font-mono uppercase tracking-wider', config.accentClass)}>
              {adaptation.modified_session_type}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
